import { getOwaResourceImageUrl } from 'owa-resource-url';
import { ControlIcons } from 'owa-control-icons';
import { getIsDarkTheme } from 'owa-fabric-theme';
import type { ComposeActionKey, HoverActionKey, ReadActionKey } from 'owa-outlook-service-options';
import { SurfaceActionIcons } from 'owa-surface-action-icons';
import { default as ArrowReply } from 'owa-fluent-icons-svg/lib/icons/ArrowReplyRegular';
import { default as ArrowReplyAll } from 'owa-fluent-icons-svg/lib/icons/ArrowReplyAllRegular';
import { default as ArrowForward } from 'owa-fluent-icons-svg/lib/icons/ArrowForwardRegular';
import { default as Emoji } from 'owa-fluent-icons-svg/lib/icons/EmojiRegular';
import { default as Delete } from 'owa-fluent-icons-svg/lib/icons/DeleteRegular';
import { default as Flag } from 'owa-fluent-icons-svg/lib/icons/FlagRegular';
import { default as Translate } from 'owa-fluent-icons-svg/lib/icons/TranslateRegular';
import { default as FolderMail } from 'owa-fluent-icons-svg/lib/icons/FolderMailRegular';
import { default as Calendar } from 'owa-fluent-icons-svg/lib/icons/CalendarLtrRegular';
import { default as ShieldError } from 'owa-fluent-icons-svg/lib/icons/ShieldErrorRegular';
import { default as Mail } from 'owa-fluent-icons-svg/lib/icons/MailRegular';
import { default as ClipboardArrowRight } from 'owa-fluent-icons-svg/lib/icons/ClipboardArrowRightRegular';
import { default as PersonAdd } from 'owa-fluent-icons-svg/lib/icons/PersonAddRegular';
import { default as MailError } from 'owa-fluent-icons-svg/lib/icons/MailErrorRegular';
import { default as MailWarning } from 'owa-fluent-icons-svg/lib/icons/MailWarningRegular';
import { default as MailAttach } from 'owa-fluent-icons-svg/lib/icons/MailAttachRegular';
import { default as PersonProhibited } from 'owa-fluent-icons-svg/lib/icons/PersonProhibitedRegular';
import { default as Print } from 'owa-fluent-icons-svg/lib/icons/PrintRegular';
import { default as ImmersiveReader } from 'owa-fluent-icons-svg/lib/icons/ImmersiveReaderRegular';
import { default as Branch } from 'owa-fluent-icons-svg/lib/icons/BranchRegular';
import { default as WindowNew } from 'owa-fluent-icons-svg/lib/icons/WindowNewRegular';
import { default as ThumbLike } from 'owa-fluent-icons-svg/lib/icons/ThumbLikeRegular';
import { default as WeatherSunny } from 'owa-fluent-icons-svg/lib/icons/WeatherSunnyRegular';
import { default as NotePin } from 'owa-fluent-icons-svg/lib/icons/NotePinRegular';
import { default as PersonChat } from 'owa-fluent-icons-svg/lib/icons/PersonChatRegular';
import { default as CalendarClock } from 'owa-fluent-icons-svg/lib/icons/CalendarClockRegular';
import AppsRegular from 'owa-fluent-icons-svg/lib/icons/AppsRegular';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getLoopResourceUrl } from 'owa-fluid-integrations/lib/utils/getLoopResourceUrl';

const ATTACH_SVG = 'attach.svg';
const ATTACHDARK_SVG = 'attachDark.svg';
const INLINEIMAGE_SVG = 'inlineimage.svg';
const INLINEIMAGEDARK_SVG = 'inlineimageDark.svg';
const EMOJI_SVG = 'emoji.svg';
const SHOWRIBBON_SVG = 'showribbon.svg';
const SHOWRIBBONDARK_SVG = 'showribbonDark.svg';
const DICTATION_SVG = 'dictate.svg';
const DICTATIONLANGUAGE_SVG = 'dictationLanguage.svg';
const SPELLING_SVG = 'spelling.svg';

export interface SurfaceActionIcon {
    iconName: string;
    fallbackIconName: string;
    isImage: boolean;
}

export function getComposeActionIconName(key: ComposeActionKey): SurfaceActionIcon {
    let iconName: string;
    let fallbackIconName: string;
    let isImage = false;
    const useFluentIcons = isFeatureEnabled('mon-fluent-icons');

    switch (key) {
        case 'AddAttachment':
            iconName = getSurfaceActionIconUrl(getIsDarkTheme() ? ATTACHDARK_SVG : ATTACH_SVG);
            fallbackIconName = getSurfaceActionIconUrl(ATTACH_SVG);
            isImage = true;
            break;
        case 'AddInlineImage':
            iconName = getSurfaceActionIconUrl(
                getIsDarkTheme() ? INLINEIMAGEDARK_SVG : INLINEIMAGE_SVG
            );
            fallbackIconName = getSurfaceActionIconUrl(INLINEIMAGE_SVG);
            isImage = true;
            break;
        case 'AddEmoji':
            iconName = getSurfaceActionIconUrl(EMOJI_SVG);
            isImage = true;
            break;
        case 'ToggleDarkCompose':
            iconName = ControlIcons.Sunny;
            break;
        case 'FluidHeroButton':
            iconName = getLoopResourceUrl();
            isImage = true;
            break;
        case 'QuickUse':
            iconName = ControlIcons.ClipboardList;
            break;
        case 'ToggleDictation':
            iconName = getSurfaceActionIconUrl(DICTATION_SVG);
            isImage = true;
            break;
        case 'SetDictationLanguage':
            iconName = getSurfaceActionIconUrl(DICTATIONLANGUAGE_SVG);
            isImage = true;
            break;
        case 'ToggleRibbon':
            iconName = getSurfaceActionIconUrl(
                getIsDarkTheme() ? SHOWRIBBONDARK_SVG : SHOWRIBBON_SVG
            );
            fallbackIconName = getSurfaceActionIconUrl(SHOWRIBBON_SVG);
            isImage = true;
            break;
        case 'SensitivityMenu':
            iconName = ControlIcons.MarkAsProtected;
            break;
        case 'ProtectMessage':
            iconName = SurfaceActionIcons.Encryption;
            break;
        case 'SaveDraft':
            iconName = ControlIcons.Save;
            break;
        case 'InsertSignature':
            iconName = SurfaceActionIcons.InsertSignatureLine;
            break;
        case 'ShowFrom':
            iconName = SurfaceActionIcons.ArrangeByFrom;
            break;
        case 'SetImportance':
            iconName = ControlIcons.Important;
            break;
        case 'ShowMessageOptions':
            iconName = ControlIcons.MailOptions;
            break;
        case 'SwitchBodyType':
            iconName = ControlIcons.PlainText;
            break;
        case 'AccChecker':
            iconName = ControlIcons.AccessibiltyChecker;
            break;
        case 'ProofingOptions':
            iconName = getSurfaceActionIconUrl(SPELLING_SVG);
            isImage = true;
            break;
        case 'FindTime':
            iconName = useFluentIcons ? CalendarClock : ControlIcons.DateTime;
            break;
        case 'MessageExtensions':
            iconName = AppsRegular;
            break;
        default:
            iconName = '';
            break;
    }

    return {
        iconName,
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2454 (146,27): Variable 'fallbackIconName' is used before being assigned.
        // @ts-expect-error
        fallbackIconName: fallbackIconName || iconName,
        isImage,
    };
}

export function getReadActionIconName(key: ReadActionKey): SurfaceActionIcon {
    let iconName: string;
    const useFluentIcons = isFeatureEnabled('mon-fluent-icons');

    switch (key) {
        case '-':
            iconName = '-';
            break;
        case 'Reply':
            iconName = useFluentIcons ? ArrowReply : ControlIcons.Reply;
            break;
        case 'ReplyAll':
            iconName = useFluentIcons ? ArrowReplyAll : ControlIcons.ReplyAll;
            break;
        case 'Forward':
            iconName = useFluentIcons ? ArrowForward : ControlIcons.Forward;
            break;
        case 'ForwardEmailAsAttachment':
            iconName = useFluentIcons ? MailAttach : ControlIcons.MailAttached;
            break;
        case 'ReplyByMeeting':
            iconName = useFluentIcons ? Calendar : ControlIcons.Calendar;
            break;
        case 'ReportAbuse':
            iconName = useFluentIcons ? ShieldError : ControlIcons.ReportHacked;
            break;
        case 'Delete':
            iconName = useFluentIcons ? Delete : ControlIcons.Delete;
            break;
        case 'MarkReadUnread':
            iconName = useFluentIcons ? Mail : ControlIcons.Mail;
            break;
        case 'FlagUnflag':
            iconName = useFluentIcons ? Flag : ControlIcons.Flag;
            break;
        case 'AssignPolicy':
            iconName = useFluentIcons ? ClipboardArrowRight : ControlIcons.Assign;
            break;
        case 'AddToSafeSenders':
            iconName = useFluentIcons ? PersonAdd : ControlIcons.AddFriend;
            break;
        case 'MarkJunkNotJunk':
            iconName = useFluentIcons ? MailError : ControlIcons.MailAlert;
            break;
        case 'MarkAsPhishing':
            iconName = useFluentIcons ? MailWarning : SurfaceActionIcons.Phishing;
            break;
        case 'Block':
            iconName = useFluentIcons ? PersonProhibited : ControlIcons.BlockContact;
            break;
        case 'CreateRule':
            iconName = useFluentIcons ? FolderMail : SurfaceActionIcons.CreateMailRule;
            break;
        case 'Print':
            iconName = useFluentIcons ? Print : ControlIcons.Print;
            break;
        case 'Translate':
            iconName = useFluentIcons ? Translate : ControlIcons.LocaleLanguage;
            break;
        case 'ShowInImmersiveReader':
            iconName = useFluentIcons ? ImmersiveReader : SurfaceActionIcons.ReadOutLoud;
            break;
        case 'ViewMessageSource':
            iconName = useFluentIcons ? Branch : ControlIcons.OpenSource;
            break;
        case 'Popout':
            iconName = useFluentIcons ? WindowNew : ControlIcons.OpenInNewWindow;
            break;
        case 'LikeUnlike':
            iconName = useFluentIcons ? ThumbLike : ControlIcons.Like;
            break;
        case 'SetReaction':
            iconName = useFluentIcons ? Emoji : ControlIcons.Emoji2;
            break;
        case 'ToggleDarkMode':
            iconName = useFluentIcons ? WeatherSunny : ControlIcons.Sunny;
            break;
        case 'ReplyByIM':
        case 'ReplyAllByIM':
            iconName = useFluentIcons ? PersonChat : ControlIcons.ChatInviteFriend;
            break;
        case 'ToggleAmp':
            iconName = ControlIcons.LightningBolt;
            break;
        case 'AddToBoard':
            iconName = useFluentIcons ? NotePin : ControlIcons.NotePinned;
            break;
        case 'MessageExtensions':
            iconName = AppsRegular;
            break;
        case 'PlayThisConversation':
            iconName = ControlIcons.MSNVideos;
            break;
        default:
            iconName = '';
            break;
    }

    return {
        iconName,
        fallbackIconName: iconName,
        isImage: false,
    };
}

export function getHoverActionIconName(key: HoverActionKey): SurfaceActionIcon {
    let iconName: string;
    switch (key) {
        case '-':
            iconName = '-';
            break;
        case 'Delete':
            iconName = ControlIcons.Delete;
            break;
        case 'Archive':
            iconName = ControlIcons.Archive;
            break;
        case 'PinUnpin':
            iconName = ControlIcons.Pinned;
            break;
        case 'ReadUnread':
            iconName = ControlIcons.Mail;
            break;
        case 'FlagUnflag':
            iconName = ControlIcons.Flag;
            break;
        case 'Move':
            iconName = ControlIcons.FabricFolder;
            break;
        default:
            iconName = '';
            break;
    }
    return {
        iconName,
        fallbackIconName: iconName,
        isImage: false,
    };
}

function getSurfaceActionIconUrl(imageName: string) {
    return getOwaResourceImageUrl('surfaceActions/' + imageName);
}
